import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import SideNavMenu from "./SideNavMenu";

type HeaderProps = {
  onChangeMenu: () => void;
};

/**
 * Shell chrome:
 * - Full-height sidebar (logo + theme) — never covered
 * - Sticky top utility bar ONLY in the main content column
 */
const Header = (props: HeaderProps) => {
  const location = useLocation();
  const [organizer, setOrganizer] = useState({
    name: "",
    email: "",
    profileImage: "",
  });
  const userData = useMemo(() => {
    const raw = localStorage.getItem("userData");
    return raw
      ? JSON.parse(raw)
      : { name: "", email: "", venueName: "" };
  }, []);
  const [showDropDown, setShowDropDown] = useState(false);
  const [mobileNavBarOpen, setMobileNavBarOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropDown(false);
      }
    };
    const handleScroll = () => setShowDropDown(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    try {
      const venueData = localStorage.getItem("venueData");
      if (venueData) {
        setOrganizer(JSON.parse(venueData));
      }
    } catch (error) {
      console.error("Error reading venue profile:", error);
    }
  }, [location.pathname]);

  const onClickLogout = () => {
    localStorage.removeItem("token");
    localStorage.clear();
    window.location.href = "/login";
  };

  const displayName = location.pathname.startsWith("/venue-event/")
    ? userData.name
    : organizer.name || "Venue";
  const displayEmail = location.pathname.startsWith("/venue-event/")
    ? userData.email
    : organizer.email;

  return (
    <>
      <SideNavMenu
        minifyNav={false}
        mobileNavOpen={mobileNavBarOpen}
        setMobileNavBarOpen={setMobileNavBarOpen}
        profileName={displayName}
        profileImage={organizer.profileImage}
      />

      {/* Sticky bar lives in .ex-main via CSS — content column only */}
      <header className="ex-content-topbar">
        <button
          type="button"
          className="ex-icon-btn d-inline-flex d-md-none"
          onClick={() => setMobileNavBarOpen(!mobileNavBarOpen)}
          title="Menu"
          aria-label="Open menu"
        >
          <i className="fa-solid fa-bars"></i>
        </button>

        <div className="ex-topbar-actions">
          {location.pathname !== "/home" && (
            <Link
              to="/home"
              className="ex-icon-btn d-none d-md-inline-flex"
              title="Home"
              style={{ width: "auto", padding: "0 14px", gap: 6, fontSize: 13, textDecoration: "none" }}
            >
              <i className="fa-solid fa-house"></i>
              <span>Home</span>
            </Link>
          )}
          <a
            className="ex-icon-btn d-none d-md-inline-flex"
            href="https://eventox.co/"
            target="_blank"
            rel="noreferrer"
            title="Explore"
            style={{ width: "auto", padding: "0 14px", gap: 6, fontSize: 13, textDecoration: "none" }}
          >
            <i className="fa-solid fa-compass"></i>
            <span>Explore</span>
          </a>

          <div className="ex-account-menu" ref={dropdownRef}>
            <button
              type="button"
              className="ex-icon-btn"
              onClick={() => setShowDropDown(!showDropDown)}
              style={{ padding: 0, overflow: "hidden" }}
            >
              {organizer.profileImage ? (
                <img
                  src={organizer.profileImage}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <i className="fa-solid fa-user"></i>
              )}
            </button>
            <div
              className={`ex-account-dropdown glass-panel shadow-premium ${
                showDropDown ? "show" : ""
              }`}
            >
              <div className="account-header">
                {organizer.profileImage ? (
                  <img
                    src={organizer.profileImage}
                    alt=""
                    className="rounded-circle"
                    style={{ width: 56, height: 56, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center bg-gradient-brand text-white mx-auto"
                    style={{ width: 56, height: 56 }}
                  >
                    <i className="fa-solid fa-user"></i>
                  </div>
                )}
                <h5>{displayName}</h5>
                <p>{displayEmail}</p>
                {location.pathname.startsWith("/venue-event/") &&
                  userData?.venue?.venueName && (
                    <p>{userData.venue.venueName}</p>
                  )}
              </div>
              {!location.pathname.startsWith("/venue-event/") && (
                <Link to="/update-venue" onClick={() => setShowDropDown(false)}>
                  My Profile
                </Link>
              )}
              <Link
                to="/login"
                onClick={() => {
                  onClickLogout();
                }}
              >
                Sign Out
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
